import type { SendDraftAttachment } from "../types/ghlBookingChat";

/**
 * Parses `data.result` from the SSE tool_call for ghl_send_message.
 *
 * SGL-244: that tool used to send on call. It now returns a draft and the send
 * happens only when the owner clicks approve, so this parser is what stands
 * between "the model wrote a message" and "a real email left the customer's
 * domain". Returns null for anything that isn't a well-formed draft — a card
 * we can't render is better than an approve button wired to junk.
 *
 * Mirrors parseAvailabilityToolResult's defensive shape: never throws.
 */
export function parseSendDraftToolResult(resultStr: string): SendDraftAttachment | null {
  try {
    const raw = JSON.parse(resultStr) as Record<string, unknown>;
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;

    // The executor returns {error, message} for not_connected / needs_reauth /
    // unavailable. Those aren't drafts and must not render an approve button.
    if (raw.status !== "draft") return null;

    const contactId = typeof raw.contact_id === "string" ? raw.contact_id : "";
    const message = typeof raw.message === "string" ? raw.message : "";
    const type = raw.type === "EMAIL" ? "EMAIL" : raw.type === "SMS" ? "SMS" : null;
    // No recipient or no body means nothing sendable, whatever else came back.
    if (!contactId || !message || !type) return null;

    const subject = typeof raw.subject === "string" ? raw.subject : "";
    // GHL sends a blank-subject email rather than refusing, and the backend
    // route rejects it — so don't offer an approve button that can only 400.
    if (type === "EMAIL" && !subject.trim()) return null;

    return {
      draft: {
        contact_id: contactId,
        contact_name: typeof raw.contact_name === "string" ? raw.contact_name : "",
        contact_handle: typeof raw.contact_handle === "string" ? raw.contact_handle : "",
        type,
        subject,
        message,
      },
    };
  } catch {
    return null;
  }
}
